import { format } from 'date-fns'
import { useTranslation } from 'react-i18next'
import { useState, useEffect, MouseEvent } from 'react'
import { Checkbox, Divider, IconButton, Popover } from '@mui/material'

import Chip from '../common/chip'
import { Document } from '../../types/document.types'
import { UserSessionType } from '../../types/user.types'
import TrashIcon from '../../assets/icons/trash-icon.svg'
import MoreMenuIcon from '../../assets/icons/more-menu-icon.svg'
import ArrowRightIcon from '../../assets/icons/arrow-right-icon.svg'
import { useLocation } from 'react-router-dom'

const DocumentsCompletedListItem = ({
  userSession,
  documentData,
  processingDocument,
  handleToggleDocumentSelection,
  handleGoToDocumentPaymentsDetails,
  handleOpenDeleteDocumentDataModal
}: {
  documentData: Document
  processingDocument: boolean
  userSession: Partial<UserSessionType>
  handleGoToDocumentPaymentsDetails: () => void
  handleOpenDeleteDocumentDataModal: (selectedDocumentData: Document) => void
  handleToggleDocumentSelection: (selectedDocumentData: Document, selected: boolean) => void
}) => {
  const { t } = useTranslation(['documents-completed-management'])

  const location = useLocation()
  const [documentFileExt, setDocumentFileExt] = useState('')
  const [documentChipStatus, setDocumentChipStatus] = useState('')
  const [uploadedByFullName, setUploadedByFullName] = useState('')
  const [documentChipStatusText, setDocumentChipStatusText] = useState('')
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null)
  const [documentNameWithoutFileExt, setDocumentNameWithoutFileExt] = useState('')

  const open = Boolean(anchorEl)
  const id = open ? 'simple-popover' : undefined

  useEffect(() => {
    if (documentData) {
      let documentNameWithoutFileExtData = ''
      const documentNameWithoutFileExtArray = documentData.originalName.split('.')

      for (let i = 0; i < documentNameWithoutFileExtArray.length - 1; i++) {
        documentNameWithoutFileExtData += documentNameWithoutFileExtArray[i]
      }

      setUploadedByFullName(String(documentData.uploadedByFullName))
      setDocumentNameWithoutFileExt(documentNameWithoutFileExtData)
      setDocumentFileExt(documentNameWithoutFileExtArray[documentNameWithoutFileExtArray.length - 1])
    }

    setDocumentChipStatus(documentData.status)

    setDocumentChipStatusText(() => {
      if (documentData.status === 'dispersing' || documentData.status === 'register-disperse-completed') {
        return t('listItem.chipStatusTexts.dispersing')
      }

      if (documentData.status.includes('error')) {
        return t('listItem.chipStatusTexts.error')
      }

      return t('listItem.chipStatusTexts.completed')
    })
  }, [documentData, t])

  return (
    <>
      <div
        onClick={handleGoToDocumentPaymentsDetails}
        className="documents-list-item-data-container"
      >
        <div className="documents-list-item-data-document-info">
          {processingDocument === false && location.pathname.indexOf('documents-completed') === -1 && (
            <Checkbox
              checked={documentData.selected ?? false}
              onClick={(event) => event.stopPropagation()}
              onChange={(event) => {
                handleToggleDocumentSelection(documentData, event.target.checked)
              }}
            />
          )}
          <div className="documents-list-item-data-document-info-name">{`${documentNameWithoutFileExt.toUpperCase()}.${documentFileExt}`}</div>
        </div>
        <div className="documents-list-item-status-container">
          <Chip
            status={documentChipStatus}
            label={documentChipStatusText}
          />
        </div>
        <div className="documents-list-item-actions-container">
          <IconButton
            edge="end"
            sx={{ color: '#107569' }}
            onClick={async (event: MouseEvent<HTMLButtonElement>) => {
              event.stopPropagation()
              setAnchorEl(event.currentTarget)
            }}
            aria-label="toggle password visibility"
          >
            <img
              src={MoreMenuIcon}
              alt="more-menu-icon"
            />
          </IconButton>
          {(userSession?.role === 'admin-client' || userSession?.role === 'super-admin-client') && documentData?.status.includes('error') && processingDocument === false && (
            <IconButton
              edge="end"
              sx={{ color: '#107569' }}
              onClick={(event) => {
                event.stopPropagation()
                handleOpenDeleteDocumentDataModal(documentData)
              }}
              aria-label="toggle password visibility"
            >
              <img
                src={TrashIcon}
                alt="trash-icon"
              />
            </IconButton>
          )}
          <div className="documents-list-item-arrow-right">
            <img
              src={ArrowRightIcon}
              alt="arrow-right-icon"
            />
          </div>
        </div>
      </div>
      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={(event: MouseEvent<HTMLButtonElement>) => {
          event.stopPropagation()
          setAnchorEl(null)
        }}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'right'
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right'
        }}
      >
        <div className="documents-list-item-popover-container">
          <div className="documents-list-item-popover">
            <strong>{t('listItem.uploadedBy')}</strong>
            <div>{uploadedByFullName}</div>
            <div>{format(documentData.createdAt, 'dd/MM/yyyy | HH:mm:ss')}</div>
          </div>
          <Divider />
          <div className="documents-list-item-popover">
            <strong>{t('listItem.approvedBy')}</strong>
            <div>{documentData.approvedByFullName?.toUpperCase()}</div>
            {documentData.approvedAt && <div>{format(documentData.approvedAt, 'dd/MM/yyyy | HH:mm:ss')}</div>}
          </div>
        </div>
      </Popover>
    </>
  )
}

export default DocumentsCompletedListItem
